import React from 'react';
import classNames from 'classnames';

import css from './CertificationIcon.module.css';

/**
 * The certification types a sitter can self-report. One icon (and one entry) per type.
 * The keys are persisted in the listing's publicData, so they must stay stable.
 */
export const CERTIFICATION_TYPES = ['professional', 'medical', 'specialist'];

export const isValidCertificationType = type => CERTIFICATION_TYPES.includes(type);

const ProfessionalIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M12 3.75 2.25 8.25 12 12.75l9.75-4.5L12 3.75Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path
      d="M6.75 10.5v5.25c0 1.24 2.35 2.25 5.25 2.25s5.25-1.01 5.25-2.25V10.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M20.25 9v5.25" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const MedicalIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M6 3v5.25a4.5 4.5 0 0 0 9 0V3"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M4.5 3H6M13.5 3H15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <path
      d="M10.5 12.75v2.25a4.5 4.5 0 0 0 9 0v-1.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="19.5" cy="11.25" r="2.25" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const SpecialistIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M4.5 20.25v-4.5a6 6 0 0 1 6-6h1.19l3.06-3.06a3 3 0 0 1 .88-.61l2.62-1.2-.9 2.7 1.9 1.42-2.4 1.8v1.2c0 1.02-.36 2-1.02 2.78l-1.83 2.16v3.31"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M9 20.25V16.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <circle cx="16.9" cy="8.4" r="0.9" fill="currentColor" />
  </svg>
);

const ICONS = {
  professional: ProfessionalIcon,
  medical: MedicalIcon,
  specialist: SpecialistIcon,
};

/**
 * A single icon representing a certification type, wrapped in a color-coded badge.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {'professional'|'medical'|'specialist'} props.type - The certification type
 * @returns {JSX.Element|null} certification icon
 */
const CertificationIcon = props => {
  const { className, rootClassName, type } = props;
  const Icon = ICONS[type];

  if (!Icon) {
    return null;
  }

  const classes = classNames(rootClassName || css.root, css[type], className);

  return (
    <span className={classes}>
      <Icon />
    </span>
  );
};

export default CertificationIcon;
